import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';

const AdaChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'ada',
      text: "Hi, I'm Ada 💛 — I'm here to support you whenever you need comfort, encouragement, or someone to talk to.\n\nWhat's been on your mind today?",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const { user } = useAuth();

  const openChat = () => {
    setIsOpen(true);
    setHasBeenOpened(true);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Ada's emotional support responses based on keywords and sentiment
  const generateAdaResponse = async (userMessage) => {
    // Try to use backend AI for more sophisticated responses
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/ada/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ message: userMessage }),
      });

      if (response.ok) {
        const data = await response.json();
        return data.response;
      }
    } catch (error) {
      console.log('Using fallback Ada responses:', error);
    }

    // Fallback to local responses if backend fails
    const lowerMessage = userMessage.toLowerCase();
    
    // Detect emotional state keywords
    const anxietyKeywords = ['anxious', 'worried', 'nervous', 'scared', 'afraid', 'panic', 'stress', 'overwhelmed'];
    const sadnessKeywords = ['sad', 'depressed', 'hopeless', 'lonely', 'isolated', 'crying', 'hurt'];
    const angerKeywords = ['angry', 'frustrated', 'mad', 'unfair', 'hate', 'upset'];
    const confusionKeywords = ['confused', 'lost', 'don\'t know', 'unsure', 'help', 'complicated'];
    const immigrationKeywords = ['visa', 'immigration', 'deportation', 'asylum', 'green card', 'citizenship', 'uscis', 'case', 'rejected', 'denied'];

    let response = "";
    let emotionalTools = [];

    // First, acknowledge and validate their feelings
    if (anxietyKeywords.some(keyword => lowerMessage.includes(keyword))) {
      response = "That sounds incredibly stressful — thank you for opening up to me. You're showing so much strength just by being here and talking about it.\n\nLet's take a deep breath together. You're safe in this moment, and you're not alone. 🌸";
      emotionalTools = [
        "🌬️ Try a 4-7-8 breathing exercise: Breathe in for 4, hold for 7, out for 8",
        "🧘‍♀️ Ground yourself: Name 5 things you can see, 4 you can touch, 3 you can hear",
        "💭 Remember: This feeling is temporary, but your strength is permanent"
      ];
    } 
    else if (sadnessKeywords.some(keyword => lowerMessage.includes(keyword))) {
      response = "I hear the heaviness in your words, and I want you to know that what you're feeling is completely valid. 💛\n\nYou've made it through so many hard days before. This moment is part of your journey, not the end of it. You matter, and your story is important.";
      emotionalTools = [
        "📝 Journal prompt: What's one small thing that brought you comfort today?",
        "🌱 Reminder: Growth often happens in the difficult moments",
        "🤗 You're not alone — so many others have walked this path and found their way"
      ];
    }
    else if (angerKeywords.some(keyword => lowerMessage.includes(keyword))) {
      response = "Your frustration is completely understandable — the immigration process can feel so unfair and overwhelming. 🌼\n\nYour feelings are valid, and it's okay to feel angry. Let's find a way to channel that energy into something that helps you feel more in control.";
      emotionalTools = [
        "💪 Channel your energy: Write down your frustrations, then write your strengths",
        "🎯 Focus on what you can control today, even if it's something small",
        "🌟 Your persistence in this difficult journey shows incredible resilience"
      ];
    }
    else if (confusionKeywords.some(keyword => lowerMessage.includes(keyword))) {
      response = "Feeling lost is so natural when everything feels complicated and uncertain. You're not alone in feeling this way. 🕊️\n\nLet's take things one step at a time. You don't have to figure everything out at once.";
      emotionalTools = [
        "📋 Break it down: What's one small step you could take today?",
        "🧭 Ask yourself: What do you need most right now? Rest? Information? Support?",
        "🌈 Remember: Clarity often comes through action, not just thinking"
      ];
    }
    else if (immigrationKeywords.some(keyword => lowerMessage.includes(keyword))) {
      response = "Immigration journeys are filled with so much uncertainty and waiting — that emotional rollercoaster is exhausting. 💙\n\nWhat you're going through takes incredible courage. Every day you keep going, you're showing remarkable strength.";
      emotionalTools = [
        "📚 Focus on what you can control: gathering documents, staying organized",
        "🤝 Connect with others who understand your journey",
        "🌟 Celebrate small victories — every step forward matters"
      ];
    }
    else {
      // General supportive response
      response = "Thank you for sharing with me. I'm here to listen and support you through whatever you're experiencing. 💛\n\nYour feelings are valid, and you're not alone in this journey.";
      emotionalTools = [
        "💝 Remind yourself: You are braver than you believe",
        "🌸 Take a moment to appreciate how far you've already come",
        "🌙 Be gentle with yourself — healing and progress take time"
      ];
    }

    // Add emotional tools as follow-up
    if (emotionalTools.length > 0) {
      response += "\n\n" + emotionalTools.join("\n\n");
    }

    response += "\n\nWould you like to talk more about what you're feeling, or would you prefer a grounding exercise or encouraging thought? I'm here for whatever you need. 🌱";

    return response;
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      sender: 'user',
      text: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate Ada thinking and responding
    setTimeout(async () => {
      const adaResponse = await generateAdaResponse(inputValue);
      
      const adaMessage = {
        id: messages.length + 2,
        sender: 'ada',
        text: adaResponse,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, adaMessage]);
      setIsTyping(false);
    }, 1500); // Simulate natural response time
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Bubble */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={openChat}
            className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white rounded-full w-16 h-16 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110"
          >
            <div className="text-center">
              <div className="text-2xl mb-1">💛</div>
              <div className="text-xs font-medium">Ada</div>
            </div>
          </button>
          
          {/* Notification badge - only show if never opened */}
          {!hasBeenOpened && (
            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
              1
            </div>
          )}
          
          {/* Helpful tooltip */}
          <div className="absolute bottom-full right-0 mb-2 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
            Need emotional support? Click to chat with Ada 💛
          </div>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-pink-500 to-purple-500 text-white p-4 flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                <span className="text-xl">💛</span>
              </div>
              <div>
                <h3 className="font-semibold text-lg">Ada</h3>
                <p className="text-xs opacity-90">Emotional Support • Always here for you</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* Minimize button */}
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-white hover:bg-opacity-30 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                title="Minimize chat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </button>
              {/* Close button */}
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-red-500 hover:bg-opacity-80 w-8 h-8 rounded-full flex items-center justify-center transition-colors bg-white bg-opacity-20"
                title="Close chat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-purple-50 to-pink-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    message.sender === 'user'
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                      : 'bg-white text-gray-800 shadow-md border border-purple-100'
                  }`}
                >
                  {message.sender === 'ada' && (
                    <div className="flex items-center mb-2">
                      <span className="text-lg mr-2">💛</span>
                      <span className="font-medium text-purple-600 text-sm">Ada</span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed whitespace-pre-line">
                    {message.text}
                  </p>
                  <p className={`text-xs mt-2 ${message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'}`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            
            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 shadow-md border border-purple-100 p-3 rounded-2xl max-w-[80%]">
                  <div className="flex items-center mb-2">
                    <span className="text-lg mr-2">💛</span>
                    <span className="font-medium text-purple-600 text-sm">Ada</span>
                  </div>
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-purple-100">
            <div className="flex items-end space-x-2">
              <div className="flex-1">
                <textarea
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Share what's on your mind..."
                  className="w-full p-3 border border-purple-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent text-gray-800 placeholder-gray-500"
                  rows="2"
                />
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping}
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 disabled:opacity-50 text-white w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            
            {/* Quick Response Buttons */}
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => setInputValue("I'm feeling anxious about my case")}
                className="px-3 py-1 bg-purple-100 text-purple-700 text-xs rounded-full hover:bg-purple-200 transition-colors"
              >
                😰 Feeling anxious
              </button>
              <button
                onClick={() => setInputValue("I need some encouragement")}
                className="px-3 py-1 bg-pink-100 text-pink-700 text-xs rounded-full hover:bg-pink-200 transition-colors"
              >
                🌟 Need encouragement
              </button>
              <button
                onClick={() => setInputValue("I'm feeling overwhelmed")}
                className="px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full hover:bg-blue-200 transition-colors"
              >
                😵 Overwhelmed
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdaChatbot;